<template>
    <div class="three-sixty">
        <LoadingAnimation v-if="isLoading" />

        <Image
            v-for="(image, index) in images"
            v-show="!isLoading && index === currentImageIndex"
            :key="index"
            :height="416"
            :width="740"
            fit="contain"
            loading="eager"
            v-bind="
                getGenericImageProps({
                    size: image.size,
                    size_x2: image.size_x2,
                })
            "
            @error="() => onload(image.url)"
            @load="() => onload(image.url)"
        />

        <div
            class="input-catcher"
            @mousedown="handleMouseDown"
            @touchstart="handleTouchStart"
            @touchmove.prevent.stop="handleTouchMove"
        ></div>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, toRefs } from 'vue'

import { useThreeSixtyImages } from './useThreeSixtyImages'
import { useThreeSixtyInput } from './useThreeSixtyInput'
import type { ThreeSixtyFragment } from '@/graphql/api'
import { getGenericImageProps } from '@/components/useImageProps'

import Image from '@/components/Image.vue'
import LoadingAnimation from '@/components/ui/LoadingAnimation.vue'

interface ThreeSixtyProps {
    images: ThreeSixtyFragment[]
    initialImageIndex?: number
}

const props = withDefaults(defineProps<ThreeSixtyProps>(), {
    initialImageIndex: 0,
})

const { images } = toRefs(props)
const loadedImgs = ref<Record<string, true>>({})

const onload = (url: string) => {
    loadedImgs.value[url] = true
}

const isLoading = computed(() =>
    images.value.some((item) => !(item.url in loadedImgs.value))
)

const { currentImageIndex } = useThreeSixtyImages(images)

currentImageIndex.value = props.initialImageIndex

const { handleTouchStart, handleTouchMove, handleMouseDown } =
    useThreeSixtyInput(currentImageIndex)
</script>

<style lang="scss" scoped>
.three-sixty {
    position: relative;
    max-width: 740px;
    width: 100%;
    height: 416px;
    user-select: none;
    cursor: grab;
    display: flex;
    align-items: center;

    :deep(.image) {
        mix-blend-mode: darken;
    }

    .input-catcher {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
    }
}
</style>
